import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { map } from 'rxjs';
import { ProgressSpinner } from 'primeng/progressspinner';
import { NcpbService } from '@core/services/ncpb.service';
import { ToastService } from '@core/services/toast.service';
import { ToastTypes } from '@core/enums/toast-types';
import { Profile } from '@core/interfaces';
import { Roles } from '@auth';

import { TranslateService, Translation } from '@ngx-translate/core';

type Identifier = {
  contextualDescription: string;
  domain: string;
  extension_value: string | null;
  birthDate?: string;
  extension_value_date?: string | null;
};

type CountryMedia = {
  image: string;
  description: string;
  extension: string;
};

@Component({
  selector: 'ncp-country',
  imports: [FormsModule, ProgressSpinner],
  templateUrl: './country.component.html',
  styleUrl: './country.component.scss',
})
export class CountryComponent implements OnInit {
  protected router = inject(Router);
  protected activatedRoute = inject(ActivatedRoute);

  private readonly ncpbService = inject(NcpbService);
  private readonly toastService = inject(ToastService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  protected translations: Translation = this.translate.instant('eu');

  protected country$ = this.activatedRoute.params.pipe(map((p) => p['country']));

  protected currentCountry?: string;
  protected selectedCountry: any;
  protected profile = JSON.parse(sessionStorage.getItem('profile') || '{}') as Profile;

  protected identifiers: Identifier[] = [];
  protected media?: CountryMedia | null;

  protected loading = false;
  protected searching = false;
  protected mediaExpanded = false;

  ngOnInit() {
    this.translate.onLangChange.subscribe(() => {
      this.translations = this.translate.instant('eu');
    });

    this.selectedCountry = JSON.parse(sessionStorage.getItem('selectedCountry') || '{}');

    this.country$.subscribe((country) => {
      this.currentCountry = country;
      this.getCountryJSON();
    });
  }

  private getCountryJSON() {
    if (!this.currentCountry) {
      return;
    }

    this.loading = true;
    this.cdr.detectChanges();

    this.ncpbService.getCountryJSON(this.currentCountry).subscribe({
      next: (response: any) => {
        this.identifiers = this.buildIdentifiers(response);
        this.media = this.buildMedia(response);
        this.mediaExpanded = false;

        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.identifiers = [];
        this.media = null;
        this.mediaExpanded = false;

        this.loading = false;
        this.cdr.detectChanges();

        this.showError(err);
      },
    });
  }

  private buildIdentifiers(countryData: any): Identifier[] {
    const spData = countryData?.country?.patientSearch;

    if (!spData?.identifier) {
      return [];
    }

    const countryName = this.selectedCountry?.countryName || countryData?.country?.friendlyName;

    if (countryName === 'Spain') {
      return [
        this.mapIdentifier(spData.identifier.ids[0].id[0]),
        this.mapIdentifier(spData.identifier.ids[0].id[1]),
        this.mapIdentifier(spData.identifier.ids[1].id),
      ].filter(Boolean) as Identifier[];
    }

    if (countryName === 'Germany') {
      const identifierGroup = this.isPharmacist()
        ? spData.identifier.ids[1].id
        : spData.identifier.ids[0].id;

      return this.toArray(identifierGroup)
        .map((identifier: any) => this.mapIdentifier(identifier))
        .filter(Boolean) as Identifier[];
    }

    if (countryName === 'Netherlands') {
      const identifier = this.mapIdentifier(spData.identifier.id);

      if (!identifier) {
        return [];
      }

      identifier.birthDate =
        spData.birthDate?.contextualDescription ||
        spData.birthDate?.['@contextualDescription'] ||
        '';

      identifier.extension_value_date = null;

      return [identifier];
    }

    if (countryName === 'Romania') {
      return [
        this.mapIdentifier(spData.identifier[0].id),
        this.mapIdentifier(spData.identifier[1].id),
      ].filter(Boolean) as Identifier[];
    }

    if (countryName === 'Italy') {
      return [
        this.mapIdentifier(spData.identifier.id[0]),
        this.mapIdentifier(spData.identifier.id[1]),
      ].filter(Boolean) as Identifier[];
    }

    return this.buildDefaultIdentifiers(spData.identifier);
  }

  private buildDefaultIdentifiers(identifier: any): Identifier[] {
    const identifiers: Identifier[] = [];

    const addIdentifier = (id: any) => {
      const mapped = this.mapIdentifier(id);

      if (mapped) {
        identifiers.push(mapped);
      }
    };

    if (Array.isArray(identifier)) {
      identifier.forEach((x: any) => addIdentifier(x.id));
    } else if (identifier.ids) {
      const ids = this.toArray(identifier.ids);

      ids.forEach((group: any) => {
        const id = group.id;

        if (Array.isArray(id)) {
          id.forEach((x: any) => addIdentifier(x));
        } else {
          addIdentifier(id);
        }
      });
    } else if (identifier.id) {
      if (Array.isArray(identifier.id)) {
        identifier.id.forEach((x: any) => addIdentifier(x));
      } else {
        addIdentifier(identifier.id);
      }
    }

    return identifiers;
  }

  private mapIdentifier(id: any): Identifier | null {
    if (!id) {
      return null;
    }

    return {
      contextualDescription:
        id.contextualDescription ||
        id['@contextualDescription'] ||
        id.originalContextualDescription ||
        id.label ||
        '',
      domain: id.domain || '',
      extension_value: null,
    };
  }

  private buildMedia(countryData: any): CountryMedia | null {
    const media = countryData?.country?.patientSearch?.media;

    if (!media?.mediaContent) {
      return null;
    }

    return {
      image: media.mediaContent,
      description: media.description || '',
      extension: String(media.fileExtension || 'png').toLowerCase(),
    };
  }

  protected isSpain(): boolean {
    const countryName = this.selectedCountry?.countryName || this.currentCountry;

    return countryName === 'Spain';
  }

  protected hasRegionalIdentifier(): boolean {
    return (
      this.hasValue(this.identifiers[0]?.extension_value) ||
      this.hasValue(this.identifiers[1]?.extension_value)
    );
  }

  protected hasNationalIdentifier(): boolean {
    return this.hasValue(this.identifiers[2]?.extension_value);
  }

  protected isFormValid(): boolean {
    if (this.identifiers.length === 0) {
      return false;
    }

    if (this.isSpain()) {
      const first = this.hasValue(this.identifiers[0]?.extension_value);

      const second = this.hasValue(this.identifiers[1]?.extension_value);

      const third = this.hasValue(this.identifiers[2]?.extension_value);

      const regionalOption = first && second && !third;

      const nationalOption = !first && !second && third;

      return regionalOption || nationalOption;
    }

    return this.identifiers.every((identifier) => this.hasValue(identifier.extension_value));
  }

  protected search = () => {
    if (!this.currentCountry || !this.isFormValid()) {
      return;
    }

    const identifiers = this.identifiers.filter((identifier) =>
      this.hasValue(identifier.extension_value)
    );

    this.searching = true;
    this.cdr.detectChanges();

    this.ncpbService.patient(this.currentCountry, identifiers).subscribe({
      next: (response: any) => {
        const patient = response?.data || response;

        sessionStorage.setItem('euPatient', JSON.stringify(patient));

        this.searching = false;
        this.cdr.detectChanges();

        this.router.navigate(['eu', this.currentCountry, 'patient']);
      },
      error: (err) => {
        this.searching = false;
        this.cdr.detectChanges();

        this.showError(err);
      },
    });
  };

  protected toggleMediaExpand = () => {
    this.mediaExpanded = !this.mediaExpanded;
  };

  protected getMediaSrc(): string {
    if (!this.media) {
      return '';
    }

    return `data:image/${this.media.extension};base64,${this.media.image}`;
  }

  private hasValue(value: string | null | undefined): boolean {
    return value !== null && value !== undefined && value.trim() !== '';
  }

  private isPharmacist(): boolean {
    return this.profile?.role?.toString() === Roles.pharmacist;
  }

  private toArray(value: any): any[] {
    return Array.isArray(value) ? value : [value];
  }

  private showError(err: any) {
    const message = err?.error?.message || err?.error?.error || err?.message || 'Unknown error';

    this.toastService.show({
      type: ToastTypes.error,
      title: 'error',
      message,
    });
  }
}
